import { useRecoilValue } from 'recoil';

import { DayOfWeekIndicator } from '@/components/day-of-week-indicator';
import { CarrierLogo } from '@/components/modules/carrier/logo';
import { EmissionsBadge } from '@/components/modules/emissions/badge';
import { VehicleTypeBadge } from '@/components/ui/vehicle-type-badge';
import { MODE_AIR } from '@/lib/constants';
import { FlightServiceModel } from '@/lib/models/flight-service/types';
import { getVehicleDescription } from '@/lib/models/vehicle/utils/get-vehicle-description';
import { getCarrierByIdState } from '@/state/carriers';
import { getVehicleByIdState } from '@/state/vehicles';
import { formatDuration } from '@/utils/format';
import { formatDateRange, formatTimeString } from '@/utils/format/date-time';
import { getPreferredIdentifier } from '@/utils/helpers';
import { tv } from '@/utils/styles';
import { getVehicleName, isFreighterAircraft } from '@/utils/vehicle';

export interface FlightServiceRowProps {
  flightService: FlightServiceModel;
  onClick: () => void;
}

const FlightServiceRow = ({ flightService, onClick }: FlightServiceRowProps) => {
  const vehicle = useRecoilValue(getVehicleByIdState(flightService.vehicleId));
  const carrier = useRecoilValue(getCarrierByIdState(flightService.carrierId));
  const carrierCode = getPreferredIdentifier(carrier.identifiers)?.value || '';
  const isFreighter = isFreighterAircraft(vehicle);
  const vehicleDescription = getVehicleDescription(vehicle);

  const { row, cell, bold, cellFixed, dateIndicator, dow } = styles();

  return (
    <tr className={row()} onClick={onClick}>
      <td aria-label={carrierCode} className={cell()}>
        <CarrierLogo variant="md" carrier={carrier} mode={MODE_AIR} />
      </td>
      <td className={cellFixed()}>
        <b className={bold()}>{flightService.flightNumber || flightService.vehicleNumber}</b>
      </td>
      <td className={cellFixed()}>{formatTimeString(flightService.departureTime)}</td>
      <td className={cellFixed()}>
        {formatTimeString(flightService.arrivalTime)}
        {flightService.arrivalDay > 0 && ` (+${flightService.arrivalDay})`}
      </td>
      <td className={cellFixed()}>{formatDuration(flightService.durationSeconds)}</td>
      <td className={cell()}>{getVehicleName(vehicle)}</td>
      <td className={cell()}>
        <VehicleTypeBadge label={vehicleDescription} isActive={isFreighter} />
      </td>
      <td className={cell()}>{flightService.emissions && <EmissionsBadge emissions={flightService.emissions} />}</td>
      <td aria-label="operating days" className={cell()}>
        <div className="relative">
          <div className={dow()}>
            <DayOfWeekIndicator operatingDays={flightService.operatingDays} />
          </div>
          <span className={dateIndicator()}>Effective: {formatDateRange(flightService.effectiveRange)}</span>
        </div>
      </td>
    </tr>
  );
};

const styles = tv({
  slots: {
    row: 'group cursor-pointer border-error bg-white text-baseSm leading-relaxed hover:shadow-card hover:outline-grey-400',
    cell: 'border-y border-grey-300 py-5 pl-4 first:rounded-bl-lg first:rounded-tl-lg first:border-l first:pl-6 last:rounded-br-lg last:rounded-tr-lg last:border-r last:pr-4 group-hover:border-grey-400',
    cellFixed: 'w-[10%] border-y border-grey-300 pl-4 first:border-l last:border-r group-hover:border-grey-400',
    bold: 'font-medium',
    dow: 'relative -top-2',
    dateIndicator:
      'absolute -bottom-[24px] left-0 right-0 block whitespace-nowrap bg-grey-50 px-1 text-center text-xs italic text-grey-500',
  },
});

export { FlightServiceRow };
